<template>
  <div class="access-groups-form">
    <OurGroup>
      <OurInput
        v-model="form.name"
        :label="$t('page.catalog.name')"
        :placeholder="$t('placeholder.newsTitle')"
        :error="nameError"
      />
      <OurTextarea
        v-model="form.description"
        :label="$t('label.shortDescription')"
        :placeholder="$t('placeholder.newsDescription')"
        rows="4"
      />
      <OurRichEditor
        v-model="form.content"
        :placeholder="'Введіть текст статті'"
        use-custom-image-handler
        @image-added="handleImageAdded"
      />
      <OurCheckbox
        v-if="!isEdit"
        v-model="form.notifyDealers"
        :label="$t('label.messageUserDealers')"
      />
      <OurCheckbox v-model="form.isActive" :label="$t('page.fleaMarket.active')" />
      <OurCheckbox v-model="form.isChangelog" :label="$t('label.isChangelog')" />
      <OurFileInput
        :label="$t('label.newsImagePreview')"
        :files="files"
        :error="imageError"
        :allowed-file-types="allowedFileTypes"
        :disabled="!!files.length"
        :preview="true"
        @changeFiles="(el) => onChangeFiles(el, 'preview')"
        @deleteFile="deleteFile"
      />
      <OurFileInput
        size="lg"
        :label="$t('label.newsImage')"
        :files="mainImage"
        :allowed-file-types="allowedFileTypes"
        :disabled="!!mainImage.length"
        :preview="true"
        @changeFiles="(el) => onChangeFiles(el, 'main')"
        @deleteFile="deleteFile"
      />
      <OurCheckbox v-model="form.showBanner" :label="$t('label.showBanner')" />
      <OurFileInput
        v-show="form.showBanner"
        size="lg"
        :label="$t('label.banner')"
        :files="banner"
        :allowed-file-types="allowedFileTypes"
        :disabled="!!banner.length"
        :preview="true"
        @changeFiles="(el) => onChangeFiles(el, 'banner')"
        @deleteFile="deleteFile"
      />
      <OurFileInput
        v-show="form.showBanner"
        size="lg"
        :label="$t('label.mobileImage')"
        :files="mobile"
        :allowed-file-types="allowedFileTypes"
        :disabled="!!mobile.length"
        :preview="true"
        @changeFiles="(el) => onChangeFiles(el, 'mobile')"
        @deleteFile="deleteFile"
      />
      <OurDatePicker v-model="form.publishedAt" :label="$t('label.date')" />
    </OurGroup>
  </div>
</template>

<script>
import { required, maxLength } from "vuelidate/lib/validators";
import ValidationService from "@/services/validation.service";
import { uploadFilesMutation } from "@/views/settings/base-knowledge/chapter/add/gql/mutations";
import apolloMixin from "@/mixins/apollo.mixin";
import DateService from "@/services/date.service";

const { TEXT_MAX_LENGTH } = ValidationService;

export default {
  name: "NewsForm",
  mixins: [apolloMixin],
  props: {
    value: {
      type: Object,
      default: () => {},
    },
    uploadFiles: {
      type: Array,
      default: () => [],
    },
    type: {
      type: String,
      default: "",
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      allowedFileTypes: [".jpg", ".jpeg", ".png"],
      files: [],
      banner: [],
      mainImage: [],
      mobile: [],
      form: {
        name: "",
        description: "",
        content: "",
        previewImage: {},
        mainImage: null,
        mobile: null,
        banner: null,
        isActive: true,
        showBanner: false,
        notifyDealers: false,
        isChangelog: false,
        publishedAt: DateService.getDateTimestamp(new Date()),
      },
      contentImages: [],
    };
  },

  validations: {
    form: {
      name: { required, maxLength: maxLength(TEXT_MAX_LENGTH) },
    },
    files: { required },
  },

  computed: {
    nameError() {
      const isDirty = this.$v.form.name.$dirty;
      const isFilled = this.$v.form.name.required;

      const maxLength = this.$v.form.name.maxLength;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      } else if (isDirty && !maxLength) {
        error = this.$t("validation.textMaxLength", {
          length: TEXT_MAX_LENGTH,
        });
      }

      return error;
    },

    imageError() {
      const isDirty = this.$v.files.$dirty;
      const isFilled = this.$v.files.required;

      let error = "";

      if (isDirty && !isFilled) {
        error = this.$t("validation.required");
      }

      return error;
    },
  },

  watch: {
    value: {
      handler: "getForm",
      deep: true,
    },
    uploadFiles: {
      handler: "getUploadFiles",
      deep: true,
    },
    files: {
      handler: "getFormFiles",
      deep: true,
    },
    banner: {
      handler: "getBanner",
      deep: true,
    },
    mobile: {
      handler: "getMobile",
      deep: true,
    },
    mainImage: {
      handler: "getMain",
      deep: true,
    },
  },

  methods: {
    getForm() {
      if (this.value) {
        this.form = { ...this.value };
      }

      this.$nextTick(() => {
        if (Object.keys(this.form.previewImage || {}).length) {
          const { id, name, link: imageUrl } = this.form.previewImage;

          if (id && name && imageUrl) {
            this.files.push({ id, name, imageUrl });
          }
        }

        if (this.form.banner) {
          const { id, name, link: imageUrl } = this.form.banner;

          if (id && name && imageUrl) {
            this.banner.push({ id, name, imageUrl });
          }
        }

        if (this.form.mainImage) {
          const { id, name, link: imageUrl } = this.form.mainImage;

          if (id && name && imageUrl) {
            this.mainImage.push({ id, name, imageUrl });
          }
        }

        if (this.form.mobile) {
          const { id, name, link: imageUrl } = this.form.mobile;

          if (id && name && imageUrl) {
            this.mobile.push({ id, name, imageUrl });
          }
        }
      });
    },

    async handleImageAdded(file, Editor, cursorLocation, resetUploader) {
      const { uploadFiles: files } = await this.$post(
        uploadFilesMutation,
        { files: file },
        { withNotify: true }
      );

      this.contentImages.push(files[0].id);

      if (files) {
        const url = files[0].link;

        Editor.insertEmbed(cursorLocation, "image", url);
        resetUploader();
      }
    },

    getFormFiles() {
      this.form.previewImage = this.files[0]?.id ? this.files[0] : {};
    },

    getBanner() {
      this.form.banner = this.banner[0]?.id ? this.banner[0] : null;
    },

    getMobile() {
      this.form.mobile = this.mobile[0]?.id ? this.mobile[0] : null;
    },

    getMain() {
      this.form.mainImage = this.mainImage[0]?.id ? this.mainImage[0] : null;
    },

    async onChangeFiles(selectedFiles, text) {
      if (selectedFiles.length) {
        await this.$emit("uploadFiles", { selectedFiles: selectedFiles, type: text });
      }
    },

    getUploadFiles() {
      const files = this.uploadFiles;

      files.forEach((file) => {
        const { id, name, link: imageUrl } = file;

        if (!id || !name || !imageUrl) return;

        if (this.type === "preview") {
          this.files.push({ id, name, imageUrl });
        } else if (this.type === "banner") {
          this.banner.push({ id, name, imageUrl });
        } else if (this.type === "mobile") {
          this.mobile.push({ id, name, imageUrl });
        } else {
          this.mainImage.push({ id, name, imageUrl });
        }
      });
    },

    async deleteFile(id) {
      await this.$emit("deleteFiles", id);
    },

    emitForm() {
      if (this.$v.$invalid) {
        this.$v.$touch();

        return;
      }

      this.form.isValid = true;
      this.form.contentImages = [...this.contentImages];
      this.$emit("input", this.form);
    },
  },
};
</script>
